import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from "react-router-dom";
import TopHeader from '../../Component/Header/TopHeader';
import { useApproveRejectMutation, useExitSIGMutation, useLazyGetSIGByIdQuery, useSwitchJoinSIGMutation } from "../../app/services/SIGServices"
import Button from '@mui/material/Button';
import Grid from "@mui/material/Grid";
import { Box, Divider, IconButton, InputAdornment, Modal, TextField, Typography } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import ViewForum from '../Admin/ViewForum';
import '../../style/css/viewSigDetail.css';
import Spinner from "../../Component/spinner/spinner"
import Event from '../../Component/Portal/Event'
import Gallery from '../../Component/Portal/Gallery';
import ImportantAnnouncementTable from "./../Admin/ImportAnnocement/ImportantAnnouncementTable";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HtmlDiff from 'htmldiff-js';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectCurrentScope } from '../../app/auth/authSlice';
import { Phone, Mail, LocationOn, AccountCircleOutlined } from "@mui/icons-material";
import userLogo from "../../assets/images/login/userLogo.jpg";
import placeholderImage from "../../assets/images/placeholder/placeholder.png";
import { selectCurrentMember } from '../../app/features/memberSlice';
import { Close } from '@material-ui/icons';

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxWidth: 500,
    minWidth: 300,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
};

/**
 * Quill (PrimeReact Editor) saves BOTH bullet and ordered lists as <ol> tags,
 * distinguishing them only via a `data-list="bullet" | "ordered"` attribute
 * on each <li>. Quill's own ql-editor CSS normally converts that attribute
 * into the correct marker, but since we render the saved HTML here via
 * dangerouslySetInnerHTML (outside the actual <Editor/>), none of that CSS
 * is present -> the browser falls back to plain numbered <ol> markers for
 * everything, including bullet lists.
 *
 * This scoped, self-contained style block replicates Quill's own list
 * rendering rules so bullet lists show as bullets and ordered lists keep
 * their numbering, without touching any external CSS files or existing
 * design/layout.
 */
const QuillListFix = () => (
    <style>{`
        .sig-description-html ol {
            padding-left: 1.5em;
            margin: 0 0 0.5em 0;
            list-style: none !important;
            counter-reset: sig-ordered-counter;
        }
        .sig-description-html ol > li {
            position: relative;
            list-style: none !important;
        }
        .sig-description-html ol > li::before {
            content: none;
        }
        .sig-description-html ol > li[data-list="bullet"]::before {
            content: "•";
            position: absolute;
            left: -1.2em;
        }
        .sig-description-html ol > li[data-list="ordered"] {
            counter-increment: sig-ordered-counter;
        }
        .sig-description-html ol > li[data-list="ordered"]::before {
            content: counter(sig-ordered-counter) ". ";
            position: absolute;
            left: -1.5em;
        }
        .sig-description-html ul {
            padding-left: 1.5em;
            margin: 0 0 0.5em 0;
        }
        .sig-description-html ul > li {
            list-style: disc;
        }
        .sig-description-html ins {
            background-color: #104e29;
            text-decoration: none;
        }
        .sig-description-html del {
            background-color: #f8d7da;
            text-decoration: line-through;
        }
    `}</style>
);

const isCompletelyDifferent = (oldHtml, newHtml) => {
    if (!oldHtml || !newHtml) return true;
    const oldText = String(oldHtml).replace(/<[^>]*>?/gm, '').trim();
    const newText = String(newHtml).replace(/<[^>]*>?/gm, '').trim();

    const stopWords = new Set(['the', 'and', 'is', 'in', 'to', 'of', 'a', 'for', 'on', 'with', 'as', 'by']);
    const getWords = (text) => text.toLowerCase().split(/\s+/).filter(w => w.length > 2 && !stopWords.has(w));

    const oldWords = getWords(oldText);
    const newWords = getWords(newText);

    if (oldWords.length === 0 || newWords.length === 0) return true;

    const oldWordsSet = new Set(oldWords);
    let matchCount = 0;
    for (const w of newWords) {
        if (oldWordsSet.has(w)) matchCount++;
    }

    const maxLen = Math.max(oldWords.length, newWords.length);
    if (maxLen === 0) return false;

    return (matchCount / maxLen) < 0.15;
};

/** Renders `text`, and if updated, highlights the entire text. */
const HighlightText = ({ text, highlighted, originalText }) => {
    if (!highlighted) return <>{text}</>;
    return <span className="bg-warning">{text}</span>;
};

/** "New" / "Removed" status badges shown on a role card. */
const RoleBadges = ({ isNew, isRemoved }) => (
    <>
        {isNew && (
            <span className="position-absolute poppins-regular top-0 end-0 badge rounded me-3 mt-3 bg-success">
                New<span className="visually-hidden">New</span>
            </span>
        )}
        {isRemoved && (
            <span style={{ opacity: '1 !important' }} className="position-absolute poppins-regular top-0 end-0 badge rounded me-3 mt-3 bg-danger">
                Removed<span className="visually-hidden">Removed</span>
            </span>
        )}
    </>
);

/**
 * A single office-bearer card. Every list in this page (new / removed /
 * changed / unchanged roles) renders through this one component instead of
 * four separate near-identical blocks of JSX.
 */
const RoleBearerCard = ({
    name, role, userId, mobile, email, address, image,
    highlight = {}, originalTextMap = {}, isAdmin, showUserId, isNew, isRemoved, dimmed,
}) => (
    <div
        className={`position-relative ${dimmed ? 'opacity-50' : ''}`}
        style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '30px 24px',
            border: '1px solid #f0eae0',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            height: '100%',
            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 12px 30px rgba(97, 31, 30, 0.12)';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.05)';
        }}
    >
        <RoleBadges isNew={isNew} isRemoved={isRemoved} />
        {/* Profile Image container with gold/beige border ring */}
        <div className={highlight?.image ? "bg-warning rounded-circle p-1" : ""}>
            <div
                style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: highlight?.image ? 'none' : '4px solid #d4af37',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f8f8'
                }}
            >
                <img
                    src={image || userLogo}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    alt={name}
                />
            </div>
        </div>

        {/* Role badge */}
        <div
            style={{
                backgroundColor: '#611f1e',
                color: '#ffffff',
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '4px 14px',
                borderRadius: '50px',
                textTransform: 'capitalize',
                marginBottom: '12px',
                letterSpacing: '0.5px'
            }}
        >
            <HighlightText text={role} highlighted={highlight.role} originalText={originalTextMap?.role} />
        </div>

        {/* Name */}
        <h4
            style={{
                fontSize: '1.2rem',
                fontWeight: '800',
                color: '#0f3d1e', // Dark green matching mockups
                margin: '0 0 16px 0',
                fontFamily: 'Georgia, serif'
            }}
        >
            <HighlightText text={name} highlighted={highlight.name} originalText={originalTextMap?.name} />
        </h4>

        {/* Separator line */}
        <div style={{ width: '40px', height: '2px', backgroundColor: '#e2d6b5', marginBottom: '16px' }} />

        {/* Contact details list */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left', fontSize: '0.85rem' }}>
            {mobile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#444' }}>
                    <Phone style={{ fontSize: '1.05rem', color: '#611f1e' }} />
                    <span><HighlightText text={mobile} highlighted={highlight.mobile} originalText={originalTextMap?.mobile} /></span>
                </div>
            )}
            {email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#444', wordBreak: 'break-all' }}>
                    <Mail style={{ fontSize: '1.05rem', color: '#611f1e' }} />
                    <span><HighlightText text={email} highlighted={highlight.email} originalText={originalTextMap?.email} /></span>
                </div>
            )}
            {address && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', color: '#444' }}>
                    <LocationOn style={{ fontSize: '1.05rem', color: '#611f1e', marginTop: '2px' }} />
                    <span><HighlightText text={address} highlighted={highlight.address} originalText={originalTextMap?.address} /></span>
                </div>
            )}
            {showUserId && userId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#888' }}>
                    <AccountCircleOutlined style={{ fontSize: '1.05rem' }} />
                    <span>ID: {userId}</span>
                </div>
            )}
        </div>

        {isAdmin && (
            <span className="position-absolute top-0 end-0 badge px-0 py-0 me-2 mt-2">
                <AdminPanelSettingsIcon className="menus-green-color" style={{ fontSize: '1.3rem' }} />
            </span>
        )}
    </div>
);

function ViewSigDetail() {
    const [loading, setLoading] = useState(false);
    const { sigGroupId } = useParams();
    const [getSigGroupDetails] = useLazyGetSIGByIdQuery();
    const [getSigData, setGetSigData] = useState([]);
    const [displayedOffice, setDisplayedOffice] = useState(2);
    const [lengthOfRole, setLengthOfRole] = useState([]);
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const [updateDetails, setUpdateDetails] = useState(null);
    const [updatedRoleDetails, setUpdatedRoleDetails] = useState([]);
    const location = useLocation();
    const currentUrl = location.pathname;
    const [approveRejectUpdate] = useApproveRejectMutation();
    const [open, setOpen] = useState(false);
    const scope = useSelector(selectCurrentScope);
    const user = useSelector(selectCurrentMember);
    const userId = user ? user._id : "";
    const [joinSwitchSig] = useSwitchJoinSIGMutation();
    const [exitSigGroup] = useExitSIGMutation();
    const [exitGroupModal, setExitGroupModal] = useState(false);

    const isSuperAdmin = scope === 'PORTAL_SUPER_ADMIN';
    const isPending = currentUrl.includes('pending');
    const removedEmails = updateDetails?.removedAdminEmail || [];
    const updatedKeys = updateDetails ? Object.keys(updateDetails) : [];

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
    } = useForm({ defaultValues: { rejectionReason: "" } });

    const handleOpen = () => setOpen(true);
    const handleClose = () => { setOpen(false); reset(); };
    const handleTabChange = (e, tabIndex) => setCurrentTabIndex(tabIndex);
    const loadMoreOffice = () => setDisplayedOffice((prev) => prev + 2);

    // Fetch the sig group details whenever the id or pending/non-pending route changes.
    useEffect(() => {
        if (!sigGroupId) return;

        setLoading(true);
        getSigGroupDetails(sigGroupId)
            .then((res) => {
                setLoading(false);
                if (!res.data?.status) return;

                const sigData = res.data.data;
                setGetSigData(sigData);

                if (isPending) {
                    const original = sigData?.roleDetails || [];
                    const updated = sigData?.pendingChanges?.roleDetails || [];

                    const existingWithUpdates = original.map((item) => {
                        const updateItem = updated.find((u) => u.email === item.email);
                        return updateItem ? { ...item, ...updateItem, isNew: false } : { ...item, isNew: false };
                    });
                    const newRoles = updated
                        .filter((u) => !original.some((o) => o.email === u.email))
                        .map((item) => ({ ...item, isNew: true }));

                    const merged = [...existingWithUpdates, ...newRoles];
                    setUpdatedRoleDetails(merged);
                    setUpdateDetails(sigData?.pendingChanges);
                    setDisplayedOffice(merged.length);
                } else {
                    setLengthOfRole(sigData?.roleDetails.length);
                }
            })
            .catch((err) => {
                setLoading(false);
                console.error(err);
            });
    }, [sigGroupId, currentUrl, getSigGroupDetails, isPending]);

    const approveReject = (type) => {
        setLoading(true);
        approveRejectUpdate({
            sigId: sigGroupId,
            type, // 0 - approved, 1 - reject
            rejectionReason: watch('rejectionReason'),
        }).then((res) => {
            setLoading(false);
            if (res?.data?.status) {
                window?.history.back();
                if (res?.data?.message === "Updated SigGroup Approved Successfully!") {
                    toast.success(res?.data?.message);
                } else {
                    toast.error(res?.data?.message);
                }
            }
        });
    };

    const joinSig = () => {
        setLoading(true);
        let data = {
            sigGroupId: sigGroupId,
            memberId: userId,
        };
        joinSwitchSig(data).then((res) => {
            if (res.data?.status) {
                toast.success(res.data?.message);
                getSigGroupDetails(sigGroupId).then((res) => {
                    setLoading(false);
                    if (res.data?.status) {
                        setGetSigData(res.data?.data);
                    }
                })

            } else {
                setLoading(false);
                toast.error(res.data?.err?.message);
            }
        });
    };
    // Builds the props for a role card when we're on the "pending" list
    // (i.e. there's an updateDetails.roleDetails to reconcile against).
    const buildPendingCardProps = (data, index) => {
        const isRemoved = removedEmails.includes(data.email);
        const original = getSigData?.roleDetails?.[index];

        if (data.isNew || isRemoved) {
            // Brand-new role, or one flagged for removal: show its own values,
            // falling back to the original entry at the same position. No highlighting.
            return {
                name: data?.name || original?.name,
                role: data?.role || original?.role,
                userId: data?.userId || original?.userId,
                mobile: data?.mobile || original?.mobile,
                email: data?.email || original?.email,
                address: data?.address || original?.address,
                image: data?.imageUrl !== undefined ? data.imageUrl : original?.imageUrl,
                highlight: {},
                isAdmin: original?.isAdmin,
                isNew: data?.isNew,
                isRemoved,
                dimmed: isRemoved,
            };
        }

        // Existing, unremoved role: highlight any field that differs from the
        // matching original entry (matched by email).
        const match = getSigData?.roleDetails?.find((item) => item.email === data?.email);
        const field = (key) => {
            const changed = match?.[key] !== data?.[key] && data?.[key] !== undefined;
            return { value: changed ? data?.[key] : original?.[key], highlighted: changed };
        };
        const nameField = field('name');
        const roleField = field('role');
        const mobileField = field('mobile');
        const emailField = field('email');
        const addressField = field('address');
        const imageField = field('imageUrl');

        return {
            name: nameField.value,
            role: roleField.value,
            userId: data?.userId || original?.userId,
            mobile: mobileField.value,
            email: emailField.value,
            address: addressField.value,
            image: imageField.value !== undefined ? (imageField.value === "" ? placeholderImage : imageField.value) : original?.imageUrl,
            highlight: {
                name: nameField.highlighted,
                role: roleField.highlighted,
                mobile: mobileField.highlighted,
                email: emailField.highlighted,
                address: addressField.highlighted,
                image: imageField.highlighted,
            },
            originalTextMap: {
                name: original?.name,
                role: original?.role,
                mobile: original?.mobile,
                email: original?.email,
                address: original?.address,
            },
            isAdmin: original?.isAdmin,
            isRemoved,
            dimmed: isRemoved,
        };
    };

    const exitSig = (sig) => {
        let data = {
            sigId: sigGroupId,
            memberId: userId,
        };
        setExitGroupModal(false);
        setLoading(true);
        exitSigGroup(data).then((res) => {
            if (res.data?.status) {
                toast.success(res.data?.message);
                getSigGroupDetails(sigGroupId).then((res) => {
                    setLoading(false);
                    if (res?.data?.status) {
                        setGetSigData(res?.data?.data);
                    }
                }).catch((err) => {
                    setLoading(false);
                    console.error(err)
                })
            } else {
                setLoading(false);
                toast.error(res.data?.err?.message);
            }
        });
    };

    return (
        <div className='page-containter'>
            <QuillListFix />
            <TopHeader />
            <div className="pt-5">
                <div className="chapter-heading">
                    <h2 className="text-center title-font text-light py-3 mt-1">View SIG Details</h2>
                </div>
            </div>
            <div className='p-md-5 p-3 overallPadding'>
                <div className='text-end'>
                    <Button onClick={() => window.history.back()} variant="contained" className='menus-color'>Back</Button>
                </div>
                {!isPending && (
                    <Grid
                        container
                        spacing={2}
                        style={{ maxWidth: "900px", margin: "0 auto", position: "sticky", top: "0", zIndex: "999", marginBlock: "24px", display: "flex", justifyContent: "center", alignItems: "center" }}
                    >
                        <Tabs
                            position="static"
                            sx={{
                                mx: 'auto',
                                width: '100%',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                boxShadow: '0 8px 24px rgba(97,31,30,0.18)',
                                backgroundColor: '#ffffff',
                                border: '2px solid #611f1e',
                                p: '4px',
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#611f1e',
                                    height: '100%',
                                    borderRadius: '6px',
                                    zIndex: 1,
                                },
                            }}
                            value={currentTabIndex}
                            onChange={handleTabChange}
                            variant="fullWidth"
                        >
                            {[
                                { label: 'About', index: 0 },
                                { label: 'Events', index: 1 },
                                { label: 'Forums', index: 2 },
                                { label: 'Gallery', index: 3 },
                                { label: 'Announcement', index: 4 },
                            ].map(({ label, index }) => (
                                <Tab
                                    key={label}
                                    label={label}
                                    className="title-font"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.9rem',
                                        transition: 'all 0.3s ease',
                                        zIndex: 2,
                                        borderRadius: '6px',
                                        mx: '2px',
                                        minHeight: '44px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        color: currentTabIndex === index ? '#ffffff !important' : '#611f1e !important',
                                    }}
                                />
                            ))}
                        </Tabs>
                    </Grid>
                )}
                {loading ? (
                    <Spinner />
                ) : (
                    <div>
                        {currentTabIndex === 0 && (
                            <>
                                <h1
                                    style={{
                                        color: '#611f1e', // Burgundy title
                                        fontFamily: 'Georgia, serif',
                                        fontWeight: '800',
                                        fontSize: '2.5rem',
                                        lineHeight: '1.2',
                                        margin: '0 0 12px 0',
                                        textAlign: "center"
                                    }}
                                >
                                    {updateDetails && Object.keys(updateDetails).includes("groupName") && getSigData?.groupName !== getSigData?.pendingChanges?.groupName ? (
                                        <HighlightText text={getSigData?.pendingChanges?.groupName} highlighted={true} originalText={getSigData?.groupName} />
                                    ) : (
                                        getSigData?.groupName
                                    )}
                                </h1>

                                <div
                                    className='about_section sig-description-html'
                                    style={{
                                        color: '#333333',
                                        textAlign: 'justify',
                                        fontSize: '1.05rem',
                                        lineHeight: '1.8',
                                        marginBottom: '48px',
                                        // padding: '0 12px'
                                    }}
                                >
                                    {updatedKeys.includes("description") ? (
                                        <div className="sig-description-container px-0">
                                            {isCompletelyDifferent(getSigData?.description, updateDetails?.description) ? (
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <div style={{ backgroundColor: '#fff5f5', border: '1px solid #ffcdd2', borderRadius: '8px', padding: '16px', height: '100%' }}>
                                                            <Typography variant="subtitle2" sx={{ color: '#d32f2f', fontWeight: 'bold', mb: 1 }}>
                                                                Previous Version
                                                            </Typography>
                                                            <div
                                                                style={{ opacity: 0.8 }}
                                                                dangerouslySetInnerHTML={{ __html: getSigData?.description || "<em>No previous description</em>" }}
                                                            />
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', padding: '16px', height: '100%' }}>
                                                            <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 1 }}>
                                                                Updated Version
                                                            </Typography>
                                                            <div
                                                                dangerouslySetInnerHTML={{ __html: updateDetails?.description || "<em>Description cleared</em>" }}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            ) : (
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: HtmlDiff.execute(
                                                            getSigData?.description || "",
                                                            updateDetails?.description || ""
                                                        )
                                                    }}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            className="sig-description-container"
                                            dangerouslySetInnerHTML={{ __html: getSigData?.description }}
                                        />
                                    )}
                                </div>

                                {/* ══ OFFICE BEARERS HEADING ══ */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                    <div style={{ flex: 1, height: '2px', background: 'linear-gradient(to right, #611f1e, transparent)' }} />
                                    <h3 style={{ color: '#611f1e', fontWeight: 800, fontSize: '1.2rem', margin: 0, whiteSpace: 'nowrap' }}>Office Bearers</h3>
                                    <div style={{ flex: 1, height: '2px', background: 'linear-gradient(to left, #611f1e, transparent)' }} />
                                </div>
                                {lengthOfRole === 0 && (
                                    <h1 className="text-secondary-new alata-bold text-capitalize">Will Be Added Shortly</h1>
                                )}
                                <div className='row mb-5'>
                                    <div className='col-md-1' />
                                    <div className='col-md-10'>
                                        <div className='row'>
                                            {updateDetails?.roleDetails
                                                ? updatedRoleDetails.slice(0, displayedOffice).map((data, index) => (
                                                    <div className="col-lg-6 col-md-6 col-sm-12 mt-5" key={data.email || index}>
                                                        <RoleBearerCard {...buildPendingCardProps(data, index)} showUserId={isSuperAdmin} />
                                                    </div>
                                                ))
                                                : (getSigData?.roleDetails || []).slice(0, displayedOffice).map((data, index) => (
                                                    <div className="col-lg-6 col-md-6 col-sm-12 mt-5" key={data.email || index}>
                                                        <RoleBearerCard
                                                            name={data?.name}
                                                            role={data?.role}
                                                            userId={data?.userId}
                                                            mobile={data?.mobile}
                                                            email={data?.email}
                                                            address={data?.address}
                                                            image={data?.imageUrl}
                                                            highlight={{}}
                                                            isAdmin={data?.isAdmin}
                                                            showUserId={isSuperAdmin}
                                                            isRemoved={removedEmails.includes(data.email)}
                                                            dimmed={removedEmails.includes(data.email)}
                                                        />
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                    <div className='col-md-1' />
                                </div>
                                {lengthOfRole > displayedOffice && (
                                    <div className='w-100 text-end mb-3'>
                                        <Button variant='contained' className="menus-color" onClick={loadMoreOffice}>Load More</Button>
                                    </div>
                                )}
                                {isPending && isSuperAdmin && (
                                    <div className="w-100 text-end mb-3">
                                        <Button variant="contained" color="error" className="rounded-0 me-3" onClick={handleOpen}>Reject</Button>
                                        <Button variant="contained" className="menus-color" onClick={() => approveReject(0)}>Approve</Button>
                                    </div>
                                )}
                            </>
                        )}
                        {currentTabIndex > 0 && (
                            <div style={{
                                maxHeight: "600px", overflowY: "auto", scrollbarWidth: "none", msOverflowStyle: "none", display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            }}>
                                <div className="row">
                                    <div className="col-md-12">
                                        {currentTabIndex === 1 && <Event />}
                                        {currentTabIndex === 2 && <ViewForum />}
                                        {currentTabIndex === 3 && <Gallery />}
                                        {currentTabIndex === 4 && <ImportantAnnouncementTable />}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {scope === "PORTAL_MEMBER" && <div className="d-flex justify-content-end">
                    {getSigData && (
                        !getSigData?.membersId?.find(
                            (mem) => mem?.id === userId
                        ) ? (
                            <Button
                                variant="contained"
                                className='menus-color'
                                onClick={() => joinSig()}
                            >
                                Join Group
                            </Button>
                        ) : getSigData?.membersId?.find(
                            (mem) =>
                                mem?.id === userId && mem?.isApproved === 1
                        ) ? (
                            <Button variant='outlined' color='error' onClick={() => setExitGroupModal(true)}>
                                Exit Group
                            </Button>
                        ) : (
                            <span className="menus-color">
                                Requested
                            </span>
                        ))}
                </div>}
            </div>
            <Modal open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box sx={style} className="d-flex flex-column gap-3 overflow-x-hidden overflow-y-hidden">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="h5" sx={{ color: "#611f1e", fontWeight: "bold" }}>
                            Reject SIG Updates
                        </Typography>
                        <IconButton onClick={handleClose} aria-label="Close">
                            <Close />
                        </IconButton>
                    </div>
                    <Box display="flex" justifyContent="center" width="100%">
                        <Divider className="black-line-for-modal mb-0" sx={{ width: "100%" }} />
                    </Box>
                    <form className="w-100" onSubmit={handleSubmit(() => approveReject(1))}>
                        <TextField
                            inputProps={{ maxLength: "300" }}
                            fullWidth
                            multiline
                            rows={5}
                            label={<Typography className="text-dark">Reason for Rejection <span style={{ color: "red" }}>*</span></Typography>}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {300 - (watch("rejectionReason") || "").length}
                                    </InputAdornment>
                                ),
                            }}
                            error={!!errors?.rejectionReason}
                            helperText={errors?.rejectionReason?.message}
                            {...register("rejectionReason", { required: 'Please enter reason for rejection' })}
                        />
                        <div className="mt-3 mx-auto" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Button variant="outlined" color="error" onClick={handleClose} style={{ marginRight: "10px" }}>Cancel</Button>
                            <Button className="menus-color" variant="contained" color="success" type='submit'>Submit</Button>
                        </div>
                    </form>
                </Box>
            </Modal>
            <Modal
                open={exitGroupModal}
                onClose={() => setExitGroupModal(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box
                    sx={style}
                    className="d-flex  flex-column gap-3 overflow-x-hidden overflow-y-hidden">
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h5" sx={{ color: "#0d4022", fontWeight: "bold" }}>
                            Confirmation
                        </Typography>
                        <IconButton onClick={() => setExitGroupModal(false)} aria-label="Close">
                            <Close />
                        </IconButton>
                    </div>
                    <Box display="flex" justifyContent="center" width="100%">
                        <Divider className="black-line-for-modal mb-0" sx={{ width: "100%" }} />
                    </Box>
                    <Typography
                        id="modal-modal-title"
                        style={{ color: "#611f1e" }}
                        variant="h6"
                        component="h2"
                        className="content-font">
                        Are You Surely Want to Exit from this SIG Group?
                    </Typography>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                            variant="outlined"
                            className="content-font"
                            style={{

                                color: "#dc3545",
                                border: "1px solid #dc3545",
                                padding: "5px 15px",
                                borderRadius: "0px",
                            }}
                            onClick={() => setExitGroupModal(false)}>
                            No
                        </Button>
                        <Button
                            className="mx-2 content-font"
                            variant="contained"
                            style={{
                                backgroundColor: "#2e7d32",
                                color: "white",
                                padding: "5px 15px",
                                borderRadius: "0px",
                            }}
                            onClick={() => exitSig()}>
                            Yes
                        </Button>
                    </div>
                </Box>
            </Modal>
        </div>
    );
}

export default ViewSigDetail;